/**
 * What? 解决什么问题
 * 为每一个动画逐一生成关键帧
 * 例如：{
            0: { opacity: 0 },
            20: { opacity: 0.5 },
            50: { opacity: 1 },
            80: { opacity: 0.5 },
        }
 *
 *
 * How? 怎么做
 * 每个order下的动画，分两种情况：1、一组动画（多个动画组成）；2、单个动画
 * 不管是一组动画，还是单个动画，针对每一个动画，主要有以下不同的case：
 *
 * case1: toStyle的属性值没有数组， 如toStyle1，则：
 * 1、merge当前动画第一帧style，生成“当前动画”第一帧的keyframe
 * 2、merge当前动画最后一帧style， 生成“当前动画”最后一帧的keyframe
 * 3、update中间帧的style，case1情况下的中间帧是由一组动画中的其他动画生成
 * 4、update当前动画最后一帧之后的关键帧的style，注意当前动画的最后一帧不一定是一组动画的最后一帧，所以要处理这种情况
 *
 *
 * case2：toStyle的属性值含有数组，如toStyle2，属性值有以下情况：
 * I: 属性值是对象数组，且对象属性含有value和 duration， 如 opacity: [{ value: 1, duration: 300 }],
 * II: 属性值是对象数组， 但对象属性只含有value，如 translateY: [{ value: 100 }, { value: 200 }],
 * III： 属性值是对象数组，对象属性有的含有value和duration， 有的只含value，如 translateX: [ { value: 100, duration: 500 }, { value: 800} ],
 * IV： 属性值不是数组，如 scaleX: 1.5,
 * 则需要经过以下几步处理：
 * 1、属性值格式化：即把不含有duration的属性，全部转换为同时含有value和duration的对象数组
 *  1-1：处理上述IV中的属性值，将其转化为对象数组。如scaleX:【{value:1.5， duration：单个动画的duration}】
 *  1-2：处理上述II和III中的属性值，将其转化为对象数组，如translateY: [{ value: 100， duration：xxxx }, { value: 200 ， duration： XXX}],
 * 2、每个属性都进行merge和update的处理：
 *  2-1：merge当前动画当前属性的第一帧
 *  2-2：属性数组的所有当前属性中间帧逐一进行merge，生成中间帧的keyframe
 *  2-3：update中间帧的style，case2情况下的中间帧可能是由当前动画其他属性的中间帧生成，也可能是由一组动画中的其他动画生成
 * 3、update当前动画最后一帧之后的关键帧的style，，注意当前动画的最后一帧不一定是一组动画的最后一帧，所以要处理这种情况
 *
 * 注意：case1 是case 2 的子类，可以统一通过case2处理，但是case1用case2的方法统一处理，会有不必要的性能浪费，因此针对case1的情况单独处理
    const toStyle1 = {
        opacity: 1,
        translateX: 100,
        translateY: 200,
    };

    const toStyle2 = {
        opacity: [{ value: 1, duration: 300 }],
        translateX: [{ value: 100, duration: 500 }, { value: 800 }],
        translateY: [{ value: 100 }, { value: 200 }],
        scaleX: 1.5,
    };
 */

/**
 * 将值为数组的属性，统一处理为含有duration的对象数组
 * @param valueList 如 [{ value: 100, duration：500 }, { value: 200 }],
 * @param duration 当前动画的持续时间ms，如持续时间2s，则为2000
 * @returns 如：[{ value: 100, duration：500 }, { value: 200， duration：1500 }],
 */
export const getValueListWithDur = (valueList, duration) => {
    const keyframeNum = valueList.length;
    if (!keyframeNum) return valueList;

    let childDurSum = 0;
    let childDurCount = 0;
    valueList.forEach((propertyObj) => {
        const { duration: perDur } = propertyObj;
        if (perDur) {
            childDurSum += perDur;
            childDurCount += 1;
        }
    });
    // 1、判断arr中的每一帧，是否有each duration
    // 相等则说明属性数组中的每一个对象都有duration，无需补齐，直接返回即可
    if (childDurCount === keyframeNum) return valueList;

    // 2.若m个帧有duration，则需找到这些duration，为没有dur的设置（duration -子duration之和）/arrLen-m
    // 不相等，则需为缺少duration的对象补齐duration
    const eachDur = (duration - childDurSum) / (keyframeNum - childDurCount);
    return valueList.map((propertyObj) => {
        const { value, duration: perDur } = propertyObj;
        if (!perDur) {
            return { value, duration: eachDur };
        }
        return propertyObj;
    });
};

/**
 * 讲style中的属性值，全部归一化为含有duration的对象数组
 * @param style 如: {scaleX: 1.5,  translateY: [{ value: 100 }, { value: 200 }]}
 * @param duration 持续时长，如：2000
 * @returns 如： {
 * scaleX: [{ value: 1.5, duration：2000 }],
 * translateY: [{ value: 100, duration：500 }, { value: 200， duration：1500 }]
 * }
 */
export const formatStyle = (style, duration) => {
    const tempStyle = style;
    Object.entries(style).forEach(([key, valueList]) => {
        if (Array.isArray(valueList)) {
            const valueListWithDur = getValueListWithDur(valueList, duration);
            const keyframeNum = valueListWithDur.length;
            if (!keyframeNum) {
                Reflect.deleteProperty(tempStyle, key);
            } else {
                tempStyle[key] = valueListWithDur;
            }
        } else {
            tempStyle[key] = [{ value: valueList, duration }];
        }
    });

    return tempStyle;
};

/**
 * 检查style对象中是否有数组
 * @param style
 * @returns boolean
 * @example style1 = {translateX: 100} => return false;
 * style2 = { translateX: [{value: 100}]} => return true;
 */
export const checkArrStyle = (style) => {
    if (!style) return false;
    const styleKeys = Object.keys(style);
    if (!styleKeys.length) return false;
    return styleKeys.some(key => Array.isArray(style[key]));
};

/**
 * 检查style对象中是否有rotate属性
 * @param style
 * @returns style1 = {translateX: 100} => return false;
 * style2 = {rotate: 100} => return true;
 */
export const checkStyleHasRotate = (style) => {
    if (!style) return false;
    const styleKeys = Object.keys(style);
    if (!styleKeys.length) return false;
    return styleKeys.some(key => key === 'rotate');
};

/**
 * 获取单个动画结束时的style
 * @param toStyle
 * @returns
 */
export const getFinalToStyle = (toStyle) => {
    if (!checkArrStyle(toStyle)) return toStyle;
    const styleKeys = Object.keys(toStyle);
    const finalToStyle = toStyle;
    styleKeys.forEach((key) => {
        const styleValue = toStyle[key];
        if (Array.isArray(styleValue)) {
            const styleValueLen = styleValue.length;
            if (!styleValueLen) {
                Reflect.deleteProperty(finalToStyle, key);
            } else {
                const { value: finalStyleValue } = styleValue[styleValueLen - 1];
                finalToStyle[key] = finalStyleValue;
            }
        }
    });

    return finalToStyle;
};

/**
 * 处理没有中间帧的简单动画
 * @param timeNodeInfo
 * @param mergeCb
 * @param updateCb
 */
export const handleSimpleStyle = (timeNodeInfo, mergeCb, updateCb) => {
    const {
        beginRercentage,
        endRercentage,
        keyframe,
        toStyle,
        fromStyle,
        hasPropertys,
        objRotate,
        lastOrderlastFrameStyle,
    } = timeNodeInfo;
    // 把当前timeNode的两条js style merge到keyframe中
    const fromParams = {
        keyframe,
        rercentage: beginRercentage,
        style: fromStyle,
        hasPropertys,
        objRotate,
        lastOrderlastFrameStyle,
    };
    mergeCb(fromParams); // 只有插入fromStyle时才考虑上一个order继承的问题
    const endParams = {
        keyframe,
        rercentage: endRercentage,
        style: toStyle,
        hasPropertys,
    };
    mergeCb(endParams);

    // 把form - to中间的rercentage结点上的style值进行调整
    updateCb(keyframe, beginRercentage, endRercentage, Object.keys(fromStyle));
};


/**
 * 复杂动画：处理timeNode起止点及起止点中间的keyframe
 * @param styleAttr 如：{key: 'translateY', valueList: [{ value: 100, duration: 2000 }]}
 * @param timeNodeInfo
 * @param mergeCb 合并关键帧回调
 * @param updateCb 更新中间帧回调
 * @returns
 */
export const handleStyleValueList = (
    styleAttr,
    timeNodeInfo,
    mergeCb = (params) => {},
    updateCb = (keyframe, curPercentage, nexPercentage, beginStyleKeys) => {},
    options = { hasRotateMidFrame: false },
) => {
    const {
        timeNode,
        beginRercentage: beginPercentage,
        endRercentage: endPercentage,
        keyframe,
        fromStyle,
        hasPropertys,
        objRotate,
        lastOrderlastFrameStyle, // 上一个order最后一个关键帧的样式
    } = timeNodeInfo;
    const duration = timeNode?.dur;
    if (!duration) return;
    const { key, valueList } = styleAttr;
    const percentageSpan = endPercentage - beginPercentage;

    // 每个order下，只有首帧需要合并上一个order最后一帧的样式, 非首帧只需合并上一帧的样式，不用管上一个order的样式
    const headKeyframeParams = {
        keyframe,
        rercentage: beginPercentage,
        style: fromStyle,
        hasPropertys,
        objRotate,
        hasRotateMidFrame: options?.hasRotateMidFrame,
        lastOrderlastFrameStyle,
    };
    mergeCb(headKeyframeParams);

    //  当前动画前一个关键帧的位置百分比，以及前一个关键帧的样式
    let lastKeyFramePercentage = beginPercentage;
    let lastKeyFrameStyle = fromStyle;
    const percentageArr = [beginPercentage];
    valueList.forEach((eachProp, index) => {
        const { duration: perDur, value: perStyleValue } = eachProp;
        const eachStep = (perDur / duration) * percentageSpan;
        if (index < valueList.length - 1) {
            // 关键帧的百分比数值保持整数
            lastKeyFramePercentage = Math.round(lastKeyFramePercentage + eachStep);
        } else {
            lastKeyFramePercentage = endPercentage;
        }
        percentageArr.push(lastKeyFramePercentage);
        lastKeyFrameStyle = { [key]: perStyleValue };
        const params = {
            keyframe,
            hasPropertys,
            objRotate,
            rercentage: lastKeyFramePercentage,
            style: lastKeyFrameStyle,
            hasRotateMidFrame: options?.hasRotateMidFrame,
        };
        mergeCb(params);
    });

    // 把form - to中间的Percentage结点上的style值进行调整
    const beginStyleKeys = Object.keys(fromStyle);
    percentageArr.forEach((curPercentage, index) => {
        const nexPercentage = percentageArr[index];
        updateCb(keyframe, curPercentage, nexPercentage, beginStyleKeys);
    });
};
/**
 * 确保rotate属性在数组尾部
 * @param toStyle
 * @returns array
 */
export const ensureRotateLastPlace = (toStyle) => {
    const toStyleEntries = Object.entries(toStyle);
    if (!checkStyleHasRotate(toStyle)) return toStyleEntries;
    let rotateIndex = -1;
    const toStyleEntriesFiltered = toStyleEntries.filter((item, index) => {
        const [key] = item;
        if (key === 'rotate') rotateIndex = index;
        return key !== 'rotate';
    });
    if (rotateIndex !== -1) {
        toStyleEntriesFiltered.push(toStyleEntries[rotateIndex]);
    }
    return toStyleEntriesFiltered;
};

/**
 * 处理含有中间帧的复杂动画
 * @param timeNodeInfo
 * @param cbs
 * @returns
 */
export const handleKeyframes = (timeNodeInfo, cbs) => {
    const { toStyle, timeNode, keyframe, endRercentage } = timeNodeInfo;
    const { mergeCb, updateCb, updateAfterCb } = cbs;
    const duration = timeNode?.dur;
    if (!duration) return;

    if (checkArrStyle(toStyle)) {
        // step1：toStyle每一个属性格式化为对象数组, [{value:XXX, duration:XXX}]
        const formattedToStyle = formatStyle(toStyle, duration);
        // step2：对toStyle中的每个属性, 进行merge 和 update
        // 需要确保rotate最后遍历
        ensureRotateLastPlace(formattedToStyle).forEach(([key, valueList]) => {
            handleStyleValueList(
                { key, valueList },
                timeNodeInfo,
                mergeCb,
                updateCb,
                {
                    hasRotateMidFrame: checkStyleHasRotate(toStyle),
                },
            );
        });
    } else {
        handleSimpleStyle(timeNodeInfo, mergeCb, updateCb);
    }

    // step3: update当前动画最后一帧之后的关键帧的style，因为当前动画的最后一帧不一定是一组动画的最后一帧
    updateAfterCb(keyframe, endRercentage, getFinalToStyle(toStyle));
};
